/**
 * Formulario canónico de lead (SCRUM-151) — rama self-service OFF.
 * Mismo contrato que landings: POST /api/leads + WhatsApp.
 */
import { useState } from "react";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { SM_PLANS } from "@/lib/pricing-catalog";
import { buildLeadWhatsAppUrl } from "@/lib/lead-whatsapp";
import { submitPlatformLead, type PlatformLeadPayload } from "@/lib/submit-platform-lead";

const PROFILES = [
  { id: "market", label: "Almacén / Mercado" },
  { id: "fast_food", label: "Comida rápida" },
  { id: "restaurant", label: "Restaurante" },
  { id: "dietetics", label: "Dietética / Naturista" },
  { id: "pharmacy", label: "Farmacia / Perfumería" },
  { id: "fashion", label: "Ropa y Moda" },
  { id: "bazar", label: "Bazar / Hogar" },
  { id: "catalog", label: "Catálogo general" },
];

const PUBLIC_PLANS = SM_PLANS.filter((p) => p.id !== "free_trial" && p.priceArs != null);

type Props = {
  defaultPlanId?: string;
  defaultRubro?: string;
  homeHref?: string;
};

export function OnboardingLeadForm({
  defaultPlanId = "pro",
  defaultRubro = "market",
  homeHref = "/",
}: Props) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<PlatformLeadPayload>({
    nombre: "",
    negocio: "",
    email: "",
    telefono: "",
    planInteresado: defaultPlanId,
    rubro: defaultRubro,
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await submitPlatformLead(form);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al enviar");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center space-y-6 py-8">
        <div className="flex justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <CheckCircle className="h-10 w-10 text-primary" />
          </div>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">¡Solicitud recibida!</h2>
          <p className="text-muted-foreground mt-2 max-w-sm mx-auto">
            Te contactamos a la brevedad para activar tu cuenta. Si no se abrió WhatsApp,{" "}
            <a href={buildLeadWhatsAppUrl(form)} target="_blank" rel="noreferrer" className="text-primary underline">
              escribinos acá
            </a>
            .
          </p>
        </div>
        <Button asChild variant="outline">
          <a href={homeHref}>← Volver a la landing</a>
        </Button>
      </div>
    );
  }

  return (
    <form className="space-y-6" onSubmit={(e) => { void submit(e); }}>
      <div>
        <h2 className="text-2xl font-bold text-foreground">Solicitá tu cuenta</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Dejanos tus datos y te contactamos para activar tu tienda.
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="lnombre">Nombre completo</Label>
            <Input
              id="lnombre"
              autoFocus
              value={form.nombre}
              onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              placeholder="Juan Pérez"
              required
              minLength={2}
            />
          </div>
          <div>
            <Label htmlFor="ltel">Teléfono (WhatsApp)</Label>
            <Input
              id="ltel"
              type="tel"
              value={form.telefono}
              onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))}
              placeholder="+54 9 11 1234-5678"
              required
            />
          </div>
        </div>
        <div>
          <Label htmlFor="lneg">Nombre del negocio</Label>
          <Input
            id="lneg"
            value={form.negocio}
            onChange={(e) => setForm((f) => ({ ...f, negocio: e.target.value }))}
            placeholder="Almacén La Esquina"
            required
            minLength={2}
          />
        </div>
        <div>
          <Label htmlFor="lemail">Email</Label>
          <Input
            id="lemail"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="juan@example.com"
            required
          />
        </div>
        <div>
          <Label>Tipo de negocio</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {PROFILES.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setForm((f) => ({ ...f, rubro: p.id }))}
                className={cn(
                  "rounded-lg border px-3 py-2 text-xs font-medium text-left transition-colors",
                  form.rubro === p.id
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:border-primary/40",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>Plan de interés</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {PUBLIC_PLANS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setForm((f) => ({ ...f, planInteresado: p.id }))}
                className={cn(
                  "rounded-lg border px-4 py-2 text-sm font-medium transition-colors",
                  form.planInteresado === p.id
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:border-primary/40",
                )}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" className="w-full sm:w-auto" disabled={loading}>
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Solicitar
      </Button>
    </form>
  );
}
